import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Handshake,
  School,
  MapPin,
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Layers,
  FlaskConical,
  Sparkles,
  Users2,
  LineChart,
  Cloud,
  GraduationCap,
  Award,
  Microscope,
} from "lucide-react";
import { PageHero, CTASection } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";

const ECOSYSTEM = [
  { icon: Layers,       t: "The PBL-TDLE academic framework" },
  { icon: FlaskConical, t: "Concept Labs and internationally sourced apparatus" },
  { icon: Microscope,   t: "Thousands of concept-based experiments" },
  { icon: Users2,       t: "Teacher training and continuous implementation support" },
  { icon: Cloud,        t: "The CoPE-App, Teacher App and Learner App" },
  { icon: LineChart,    t: "Cloud-based academic monitoring and learning analytics" },
  { icon: CheckCircle2, t: "Structured assessments and real-time progress tracking" },
  { icon: Sparkles,     t: "Research-scientist mentorship" },
  { icon: Award,        t: "Multi-level national and international problem-solving" },
  { icon: GraduationCap,t: "The NextGen Training Program for eligible students" },
  { icon: School,       t: "Preparation for Olympiads, JEE, NEET and international exams" },
  { icon: ArrowUpRight, t: "Research, innovation and international university guidance" },
];

export default function OurPartners() {
  return (
    <div data-testid="our-partners-page">
      <PageHero
        n="P"
        label="Our Partners"
        lines={["Together, Transforming", "the Way Students Learn."]}
        intro="THECREATOR Education collaborates with forward-thinking schools that share our commitment to transforming education from passive learning into active concept development, problem-solving, research and innovation."
      />

      {/* A · Intro editorial */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-current/20 rounded-full px-4 py-2 mb-6">
              <Handshake className="w-4 h-4 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase opacity-70">A shared vision</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight font-light">
              Every student should understand every concept, <span className="text-accent">participate actively</span> and receive the opportunity to develop their full potential.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <p className="text-lg leading-relaxed opacity-75">
              Our partner schools implement the PBL-TDLE Framework: <span className="font-semibold">Problem-Based Learning through Teaching by Demonstrations and Learning by Experiments</span>.
            </p>
            <p className="leading-relaxed opacity-70 mt-4">
              Through this partnership, classrooms become dynamic learning environments where teachers demonstrate concepts, students investigate them through experiments and every learner develops the ability to apply knowledge confidently.
            </p>
          </Reveal>
        </div>

        {/* Watch — the shared vision in action */}
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid md:grid-cols-12 gap-8 items-start" data-testid="shared-vision-videos">
            <Reveal className="md:col-span-4 lg:col-span-3">
              {/* Mobile-recorded short — vertical phone frame */}
              <div className="w-full max-w-[300px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid="shared-vision-short">
                <YouTubeEmbed id="ubfe05Ntbqs" title="A shared vision — PBL-TDLE classrooms in action" short />
              </div>
            </Reveal>
            <Reveal delay={0.08} className="md:col-span-8 lg:col-span-9">
              <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid="shared-vision-video">
                <YouTubeEmbed id="xi_CH9mIutM" title="A shared vision — partnering with THECREATOR Education" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">Our Partner Schools</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Schools already <span className="text-accent">transforming classrooms</span> with PBL-TDLE.
          </h2>

          {/* Prominent reach banner */}
          <Reveal>
            <div className="mt-12 rounded-3xl border-2 dk-panel relative overflow-hidden p-8 md:p-12" style={{ borderColor: "rgba(42,167,212,0.5)" }} data-testid="partners-reach-banner">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
              <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: "#F0A13C" }} />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-c-amber" />
                  <span className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">Operational Across Maharashtra</span>
                </div>
                <p className="font-display text-2xl md:text-4xl dk-text leading-tight tracking-tight mt-5 max-w-4xl">
                  The PBL-TDLE Framework is operational in <span className="text-c-amber">multiple schools across Maharashtra</span> — including Cambridge schools in Mumbai and CBSE schools.
                </p>
                <div className="flex flex-wrap gap-3 mt-8">
                  {["Cambridge Schools · Mumbai", "CBSE Schools", "Schools Across Maharashtra"].map((c) => (
                    <span key={c} className="font-mono-label text-[10px] tracking-label uppercase border dk-border rounded-full px-4 py-2 dk-text-soft">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Founding & Model School — highlighted spotlight */}
          <Reveal delay={0.08}>
            <div className="mt-8 rounded-3xl relative overflow-hidden" data-testid="founding-school-highlight" style={{ backgroundColor: "#050B14", border: "2px solid rgba(240,161,60,0.55)", boxShadow: "0 0 40px -12px rgba(240,161,60,0.4)" }}>
              <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: "linear-gradient(to bottom, #F0A831, transparent)" }} />
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-30" style={{ backgroundColor: "#F0A831" }} />
              <div className="absolute -bottom-8 -right-4 font-display text-[9rem] leading-none tracking-tighter opacity-[0.06] select-none pointer-events-none" style={{ color: "#F0A831" }} aria-hidden>TCIS</div>
              <div className="relative p-8 md:p-12 grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <span className="inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-label uppercase px-3 py-1.5 rounded-full" style={{ border: "1px solid rgba(240,161,60,0.55)", backgroundColor: "rgba(240,161,60,0.15)", color: "#F0A831" }}>
                    <Sparkles className="w-3 h-3" /> Founding & Model School
                  </span>
                  <h3 className="font-display text-3xl md:text-4xl leading-tight tracking-tight mt-6" style={{ color: "#ffffff" }}>
                    The Creator International School
                  </h3>
                  <div className="inline-flex items-center gap-1.5 mt-3 font-mono-label text-[11px] tracking-label uppercase" style={{ color: "rgba(255,255,255,0.6)" }}>
                    <MapPin className="w-3.5 h-3.5" style={{ color: "#F0A831" }} /> Khamgaon · Cambridge International School
                  </div>
                  <p className="text-[15px] leading-relaxed mt-5 max-w-2xl" style={{ color: "rgba(255,255,255,0.72)" }}>
                    The founding and model school of the PBL-TDLE Framework — where classrooms function as Concept Labs and students develop concepts through demonstrations, experiments and structured problem-solving from the early years onwards.
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <div className="inline-flex flex-col items-start lg:items-end gap-4">
                    <div className="rounded-2xl flex items-center justify-center font-display text-2xl tracking-tight" style={{ width: 84, height: 84, color: "#F0A831", backgroundColor: "rgba(240,161,60,0.12)", border: "1px solid rgba(240,161,60,0.4)" }} aria-hidden>TCIS</div>
                    <a
                      href="https://www.the-creator.net"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase rounded-full border px-5 py-2.5 transition-colors hover:bg-white/5"
                      style={{ borderColor: "rgba(240,161,60,0.55)", color: "#F0A831" }}
                      data-testid="founding-school-website"
                    >
                      Visit the-creator.net <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Watch — our partner schools in action */}
          <div className="mt-14 grid md:grid-cols-12 gap-8 items-start" data-testid="partner-schools-videos">
            <div className="md:col-span-9 grid sm:grid-cols-2 gap-8">
              {[
                { id: "skjsIZeU6oI", t: "Partner school classrooms — data analysis in a live experiment" },
                { id: "k0EviptnLrs", t: "Partner school classrooms — mathematics as a laboratory subject" },
              ].map((v, i) => (
                <Reveal key={v.id} delay={i * 0.08}>
                  <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid={`partner-schools-video-${i}`}>
                    <YouTubeEmbed id={v.id} title={v.t} />
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.16} className="md:col-span-3">
              {/* Mobile-recorded short — vertical phone frame */}
              <div className="w-full max-w-[280px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid="partner-schools-short">
                <YouTubeEmbed id="nN1IcQK8ziw" title="Partner school classrooms — demonstration-based learning in action" short />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="border-t border-white/10" data-testid="partners-more-than">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">More Than an Educational Service</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl mt-6 tracking-tight leading-tight max-w-5xl font-light">
            Not institutions using our programs — <span className="text-accent">long-term partners</span> in educational transformation.
          </h2>
          <p className="leading-relaxed opacity-75 mt-6 max-w-4xl">
            Every partnership includes academic planning, teacher development, research-scientist mentorship, implementation support, concept-based resources, apparatus, experiments, assessment systems and technology-enabled monitoring. Our team works closely with school leaders, coordinators and teachers to ensure the framework is implemented effectively and produces <span className="font-semibold">measurable improvements in student learning</span>.
          </p>

          <div className="mt-14">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-accent mb-6">Complete Digital Academic Ecosystem</div>

            {/* Featured CoPE-App block — full-width, elevated */}
            <Reveal
              className="relative rounded-2xl p-7 md:p-9 overflow-hidden mb-4"
              data-testid="ecosystem-cope-featured"
              style={{ backgroundColor: "#050B14", color: "#ffffff", border: "1px solid rgba(240,168,49,0.35)" }}
            >
              <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full blur-3xl opacity-25 pointer-events-none" style={{ backgroundColor: "#F0A831" }} />
              <div className="relative grid md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-4 flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: "#F0A831", backgroundColor: "rgba(240,168,49,0.15)" }}>
                    <Cloud className="w-6 h-6" style={{ color: "#F0A831" }} />
                  </div>
                  <div>
                    <div className="font-mono-label text-[10px] tracking-label uppercase text-accent">The Digital Core</div>
                    <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight mt-2" style={{ color: "#ffffff" }}>
                      CoPE-App, Teacher App &amp; Learner App
                    </div>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <p className="leading-relaxed text-[15px]" style={{ color: "rgba(255,255,255,0.85)" }}>
                    For <span className="font-semibold" style={{ color: "#ffffff" }}>every grade (1 to 12)</span> and <span className="font-semibold" style={{ color: "#ffffff" }}>all four subjects — Physics, Chemistry, Biology and Mathematics</span> — the CoPE-App generates a purpose-built digital lesson plan that includes:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2 mt-4">
                    {[
                      { icon: Layers,       t: "Complete list of concepts" },
                      { icon: Sparkles,     t: "Curated problem sets (curriculum → advanced → olympiad)" },
                      { icon: Microscope,   t: "Concept-linked experiments" },
                      { icon: FlaskConical, t: "Exact apparatus for each experiment" },
                      { icon: CheckCircle2, t: "Detailed step-by-step solutions to every problem" },
                      { icon: LineChart,    t: "Per-student mastery tracking on every concept" },
                    ].map((x, k) => {
                      const Ic = x.icon;
                      return (
                        <div key={x.t} className="flex items-start gap-2.5 rounded-lg px-3 py-2" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                          <Ic className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#F0A831" }} />
                          <span className="text-sm leading-snug" style={{ color: "rgba(255,255,255,0.9)" }}>{x.t}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="leading-relaxed text-sm italic mt-4" style={{ color: "rgba(255,255,255,0.6)" }}>
                    One integrated cloud platform — a Teacher Portal, a Student App and a School Leader dashboard — all in sync.
                  </p>
                  <a
                    href="https://tce-pbl.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-5 font-mono-label text-[11px] tracking-label uppercase rounded-full border px-4 py-2 transition-colors hover:bg-white/5"
                    style={{ borderColor: "rgba(240,168,49,0.6)", color: "#F0A831" }}
                    data-testid="cope-website-link"
                  >
                    Explore the platform · tce-pbl.uk
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ECOSYSTEM.filter(e => !e.t.startsWith("The CoPE-App")).map((e, i) => {
                const Icon = e.icon;
                return (
                  <Reveal
                    key={e.t}
                    delay={(i % 6) * 0.04}
                    className="flex items-start gap-3 border rounded-xl px-4 py-3"
                    style={{ backgroundColor: "#050B14", borderColor: "rgba(255,255,255,0.1)", color: "#ffffff" }}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center border border-accent/40 bg-accent/10 shrink-0">
                      <Icon className="w-4 h-4 text-accent" />
                    </div>
                    <span className="text-sm md:text-base leading-snug" style={{ color: "rgba(255,255,255,0.9)" }}>{e.t}</span>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* D · Shared mission — closing */}
      <section className="border-t border-white/10" data-testid="partners-mission">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="✦">A Shared Mission</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl mt-6 tracking-tight leading-tight max-w-5xl font-light">
            Students who do not merely read, listen and remember — they <span className="text-accent">investigate, verify, understand, apply, question, solve</span> and discover.
          </h2>

          <div className="mt-10 grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-8">
              <p className="leading-relaxed opacity-75">
                Together with our partner schools, we are building an educational system in which students actively develop the abilities of the researchers, scientists, innovators, technologists, professionals and responsible leaders of the future — capable of contributing meaningfully to <span className="font-semibold">national development and the advancement of humanity</span>.
              </p>

              <Reveal delay={0.05} className="mt-8 rounded-2xl p-8 md:p-10 border-l-4 border-accent" style={{ backgroundColor: "#050B14", color: "#ffffff" }}>
                <p className="font-display text-2xl md:text-4xl leading-tight tracking-tight" style={{ color: "#ffffff" }}>
                  <span style={{ color: "#ffffff" }}>Our partners are not simply adopting a learning framework. </span><span className="text-accent">They are joining a movement to redefine education.</span>
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <Link to="/why-pbl-tdle" className="inline-flex items-center justify-between gap-2 border border-current/20 rounded-full px-6 py-4 hover:border-accent hover:text-accent transition-colors group" data-testid="partners-cta-why">
                <span className="font-mono-label text-[11px] tracking-label uppercase">Why PBL-TDLE?</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/enroll" className="inline-flex items-center justify-between gap-2 bg-accent text-[#050B14] rounded-full px-6 py-4 font-mono-label text-[11px] tracking-label uppercase hover:bg-accent/90 transition-colors group" data-testid="partners-cta-enroll">
                <span>Enroll Your School</span>
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Watch — the shared mission in action */}
          <div className="grid md:grid-cols-2 gap-8 mt-14" data-testid="shared-mission-videos">
            {[
              { id: "K1ne5qrDPtQ", t: "A Shared Mission — partnering with THECREATOR Education" },
              { id: "gG6hPzfz-Lo", t: "A Shared Mission — inside PBL-TDLE classrooms" },
            ].map((v, i) => (
              <Reveal key={v.id} delay={i * 0.08}>
                <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid={`shared-mission-video-${i}`}>
                  <YouTubeEmbed id={v.id} title={v.t} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
